// The loading skeleton, shaped like the chrome the app is about to draw: the header bar,
// the tab strip, a view bar, and a list.
//
// It draws the REAL tabs from `lib/views`, not grey placeholders. Their labels are static
// (only the counts wait on data), so showing them means the swap to the live app moves
// nothing: the strip is already in place and only the sheet fills in. The active tab comes
// off `location.hash`, since this renders before the router mounts.
//
// Placeholder fills use --hover so they read the same in light and dark.

import { STRIP_CLASS, TAB_CLASS, TABS, hueVar, tabForHash, tabStyle } from '../lib/views'
import { Icon } from './Icon'
import { Brand } from './Mark'

function Bar({
  w,
  h = 12,
  className = '',
  style,
}: {
  w: number | string
  h?: number
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div
      className={`rounded-inline animate-pulse ${className}`}
      style={{
        width: typeof w === 'number' ? `${w}px` : w,
        height: h,
        background: 'var(--hover)',
        ...style,
      }}
    />
  )
}

const FRAME = 'mx-auto w-full max-w-[1180px] px-5 sm:px-8'

export function AppSkeleton() {
  const active = typeof window === 'undefined' ? undefined : tabForHash(window.location.hash)
  // A record route (`/e/…`) belongs to a tab we can't know without the store, so nothing
  // is underlined rather than the wrong thing.
  return (
    <div className="flex h-full flex-col" style={{ background: 'var(--bg)' }} aria-hidden>
      <header
        className="shrink-0 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--rail)' }}
      >
        <div className="flex items-center gap-3 px-3 pt-2 sm:px-4">
          <span className="flex shrink-0 items-center gap-2">
            <Brand size={24} />
            <span className="text-lead hidden font-semibold tracking-tight sm:inline">Crum</span>
          </span>
          <span
            className="rounded-control text-meta flex min-w-0 flex-1 items-center gap-2 border px-2.5 py-1 sm:max-w-[280px]"
            style={{
              borderColor: 'color-mix(in srgb, var(--fg) 12%, transparent)',
              color: 'var(--muted)',
              background: 'color-mix(in srgb, var(--panel) 55%, transparent)',
            }}
          >
            <Icon name="Search" size={14} />
            <span className="truncate">Jump to anything</span>
            <kbd
              className="rounded-inline ml-auto hidden px-1.5 sm:inline"
              style={{
                background: 'var(--rail-hover)',
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
              }}
            >
              ⌘K
            </kbd>
          </span>
          <span
            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center"
            style={{ color: 'var(--faint)' }}
          >
            <Icon name="Settings" size={16} />
          </span>
        </div>

        <nav className={STRIP_CLASS}>
          {TABS.map((tab) => {
            const on = tab.to === active?.to
            return (
              <span key={tab.to} className={TAB_CLASS} style={tabStyle(tab, on)}>
                {/* Same `sm:hidden` as the real tab: labels alone on a wide window. */}
                <Icon
                  name={tab.icon}
                  size={14}
                  className="sm:hidden"
                  style={{ color: on ? hueVar(tab) : 'var(--faint)' }}
                />
                {tab.label}
                {/* A count belongs to data, so it can't be drawn yet. The placeholder is
                    one mono digit wide (6.33px measured), which is what most of these
                    counts are: the strip then settles by a pixel or two, not by a tab. */}
                {tab.kind && <Bar w={6.33} h={9} style={{ opacity: 0.7 }} />}
              </span>
            )
          })}
        </nav>
      </header>

      <main className="ground min-w-0 flex-1 overflow-auto">
        {/* The view bar: lens chips, whose labels and counts both wait on data. */}
        <div className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
          <div className={`${FRAME} flex items-center gap-2.5 py-2`}>
            {[54, 44, 62].map((w, i) => (
              <Bar
                key={i}
                w={w}
                h={21}
                className="rounded-full"
                style={{ opacity: 1 - i * 0.25 }}
              />
            ))}
          </div>
        </div>

        <div className={`${FRAME} pt-4 pb-16`}>
          {/* Column heads, then rows: a name, a subline, and two right-hand cells. */}
          <div className="flex items-center gap-2.5 px-3 pb-2">
            <Bar w={64} h={9} style={{ opacity: 0.7 }} />
            <Bar w={40} h={9} style={{ opacity: 0.7 }} />
            <div className="ml-auto flex items-center gap-2.5">
              <Bar w={44} h={9} style={{ opacity: 0.7 }} />
              <Bar w={52} h={9} style={{ opacity: 0.7 }} />
            </div>
          </div>
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 border-t px-3 py-[9px]"
              style={{ borderColor: 'var(--border)' }}
            >
              <Bar w={140 + (i % 3) * 30} h={13} />
              <Bar w={90 - (i % 4) * 12} h={11} style={{ opacity: 0.6 }} />
              <div className="ml-auto flex items-center gap-2.5">
                <Bar w={36} h={11} style={{ opacity: 0.6 }} />
                <span
                  className="h-[18px] w-[18px] animate-pulse rounded-full"
                  style={{ background: 'var(--hover)' }}
                />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  )
}
